/**
 * Shared helpers for the BI dashboard views.
 */

/** Formats a Date to the `YYYY-MM-DD` key expected by the BI API. */
export function toApiDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

/** Today as `YYYY-MM-DD`. */
export function today(): string {
    return toApiDate(new Date());
}

/** `YYYY-MM-DD` for `days` ago. */
export function daysAgo(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return toApiDate(d);
}

/** First day of the current month as `YYYY-MM-DD`. */
export function startOfMonth(): string {
    const d = new Date();
    d.setDate(1);
    return toApiDate(d);
}

/** First day of the current year as `YYYY-MM-DD`. */
export function startOfYear(): string {
    const d = new Date();
    d.setMonth(0, 1);
    return toApiDate(d);
}

/** Compact currency formatting (EUR, French locale). */
export function formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return `${Number(value).toLocaleString('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })} €`;
}

/** Number formatting (French locale). */
export function formatNumber(value: number | null | undefined, decimals = 0): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return Number(value).toLocaleString('fr-FR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/** Percentage formatting; returns `-` when undefined. */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return `${Number(value).toFixed(decimals)}%`;
}

/**
 * Color band for a margin/markup rate, mirroring the legacy margin list scale.
 * Returns a Tailwind text class.
 */
export function marginRateColor(rate: number | null | undefined): string {
    if (rate === null || rate === undefined) return 'text-gray-400';
    if (rate < 5) return 'text-red-600';
    if (rate < 15) return 'text-orange-500';
    if (rate < 30) return 'text-yellow-500';
    if (rate < 50) return 'text-lime-600';
    return 'text-green-600';
}

/** Background badge class for a margin/markup rate. */
export function marginRateBadge(rate: number | null | undefined): string {
    if (rate === null || rate === undefined) return 'bg-gray-100 text-gray-500';
    if (rate < 5) return 'bg-red-100 text-red-700';
    if (rate < 15) return 'bg-orange-100 text-orange-700';
    if (rate < 30) return 'bg-yellow-100 text-yellow-700';
    if (rate < 50) return 'bg-lime-100 text-lime-700';
    return 'bg-green-100 text-green-700';
}

/** Quick-range presets shared by the filter bar. */
export const QUICK_RANGES: Array<{ label: string; value: string }> = [
    { label: '7j', value: '7d' },
    { label: '30j', value: '30d' },
    { label: '90j', value: '90d' },
    { label: 'Mois', value: 'month' },
    { label: 'Année', value: 'year' },
];

/** Resolves a quick-range preset to an inclusive `{ from, to }` pair. */
export function resolveQuickRange(value: string): { from: string; to: string } {
    switch (value) {
        case '7d':
            return { from: daysAgo(7), to: today() };
        case '30d':
            return { from: daysAgo(30), to: today() };
        case '90d':
            return { from: daysAgo(90), to: today() };
        case 'month':
            return { from: startOfMonth(), to: today() };
        case 'year':
            return { from: startOfYear(), to: today() };
        default:
            return { from: daysAgo(30), to: today() };
    }
}
